/**
 * Hoffmaster Location Map Widget Handler
 *
 * Initializes Leaflet.js maps with location markers and a horizontal carousel
 * of location cards overlaid on the map.
 *
 * Features:
 * - Renders interactive map with custom markers
 * - Horizontal scrollable carousel with arrow navigation
 * - Auto-selects first location on load
 * - Selected location (closest to left edge) updates map view
 * - Arrow clicks scroll to next/previous location
 * - Smooth scroll animations
 */

class HoffmasterLocationMapHandler extends elementorModules.frontend.handlers.Base {
	getDefaultSettings() {
		return {
			selectors: {
				mapWrapper: '.hm-location-map',
				mapContainer: '.hm-location-map__map',
				mapView: '.hm-location-map__map-view',
				listView: '.hm-location-map__list-view',
				tabs: '.hm-location-map__tabs',
				tab: '.hm-location-map__tab',
				carouselContainer: '.hm-location-map__locations',
				carousel: '.hm-location-map__carousel',
				arrowsContainer: '.hm-location-map__arrows-container',
				prevArrow: '.hm-location-map__arrow--prev',
				nextArrow: '.hm-location-map__arrow--next',
				locationCard: '.hm-location-card',
			},
		};
	}

	getDefaultElements() {
		const selectors = this.getSettings('selectors');
		return {
			$mapWrapper: this.$element.find(selectors.mapWrapper),
			$mapContainer: this.$element.find(selectors.mapContainer),
			$mapView: this.$element.find(selectors.mapView),
			$listView: this.$element.find(selectors.listView),
			$tabs: this.$element.find(selectors.tabs),
			$tab: this.$element.find(selectors.tab),
			$carouselContainer: this.$element.find(selectors.carouselContainer),
			$carousel: this.$element.find(selectors.carousel),
			$arrowsContainer: this.$element.find(selectors.arrowsContainer),
			$prevArrow: this.$element.find(selectors.prevArrow),
			$nextArrow: this.$element.find(selectors.nextArrow),
			$locationCards: this.$element.find(selectors.locationCard),
		};
	}

	bindEvents() {
		// Check if Leaflet is loaded
		if (typeof L === 'undefined') {
			console.error('Location map: Leaflet.js not loaded');
			return;
		}

		// Get display mode
		const mapWrapper = this.elements.$mapWrapper[0];
		this.displayMode = mapWrapper ? mapWrapper.dataset.displayMode : 'map_only';

		// Initialize tabs if in 'both' mode
		if (this.displayMode === 'both' && this.elements.$tabs.length) {
			this.initTabs();
		}

		// Initialize map if in map mode
		if (
			(this.displayMode === 'map_only' || this.displayMode === 'both') &&
			this.elements.$mapContainer.length
		) {
			this.initMap();

			// Initialize carousel if present
			if (this.elements.$carouselContainer.length) {
				this.initCarousel();
			}
		}

		// Handle window resize
		this.bindResizeEvents();
	}

	initTabs() {
		// Get default tab from data attribute
		const mapWrapper = this.elements.$mapWrapper[0];
		const defaultTab = mapWrapper ? mapWrapper.dataset.defaultTab : 'map';

		// Set initial active tab
		this.activeTab = defaultTab;

		// Tab click handlers
		this.elements.$tab.each((index, tab) => {
			jQuery(tab).on('click', (e) => {
				e.preventDefault();
				const tabName = jQuery(tab).data('tab');
				this.switchTab(tabName);
			});
		});
	}

	switchTab(tabName) {
		if (this.activeTab === tabName) return;

		this.activeTab = tabName;

		// Update tab active states
		this.elements.$tab.each((i, tab) => {
			const $tab = jQuery(tab);
			const isActive = $tab.data('tab') === tabName;
			$tab.toggleClass('hm-location-map__tab--active', isActive);
		});

		// Toggle view visibility
		if (tabName === 'map') {
			this.elements.$mapView.show();
			this.elements.$listView.hide();
			// Invalidate map size after showing
			setTimeout(() => {
				if (this.map) {
					this.map.invalidateSize();
				}
			}, 100);
		} else {
			this.elements.$mapView.hide();
			this.elements.$listView.show();
		}
	}

	initMap() {
		const mapWrapper = this.elements.$mapWrapper[0];
		const mapContainer = this.elements.$mapContainer[0];

		// Get widget data
		const locationsData = JSON.parse(mapWrapper.dataset.locations || '[]');
		const defaultLat = parseFloat(mapWrapper.dataset.defaultLat || '39.8283');
		const defaultLng = parseFloat(mapWrapper.dataset.defaultLng || '-98.5795');
		const defaultZoom = parseInt(mapWrapper.dataset.defaultZoom || '4', 10);

		if (locationsData.length === 0) {
			console.warn('Location map: No locations with coordinates found');
			mapContainer.innerHTML =
				'<div class="hm-location-map__empty"><p>No locations available</p></div>';
			return;
		}

		// Initialize Leaflet map
		this.map = L.map(mapContainer.id, {
			scrollWheelZoom: false,
			zoomControl: true,
		}).setView([defaultLat, defaultLng], defaultZoom);

		// Add tile layer
		L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_3hec_1_2b03b997ac69e694bd68ef67', {
			attribution:
				'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
			subdomains: 'abcd',
			maxZoom: 19,
		}).addTo(this.map);

		// Store markers
		this.markers = [];
		const markerBounds = L.latLngBounds();

		// Add markers for each location
		locationsData.forEach((location) => {
			const latLng = L.latLng(location.lat, location.lng);
			markerBounds.extend(latLng);

			const markerLabel = location.address
				? `${location.title} — ${location.address}`
				: `Map marker for ${location.title}`;

			const svgTitleId = `hm-marker-title-${location.id}`;

			const markerIcon = L.divIcon({
				className: 'hm-map-marker',
				iconSize: [32, 42],
				iconAnchor: [16, 42],
				popupAnchor: [0, -44],
				html: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42" fill="none"
                    role="img"
                    aria-labelledby="${svgTitleId}"
                    focusable="false">
                    <title id="${svgTitleId}">${markerLabel}</title>
                    <path d="M16 0C7.163 0 0 7.163 0 16c0 10.444 14.222 24.597 15.164 25.52a1.143 1.143 0 0 0 1.672 0C17.778 40.597 32 26.444 32 16 32 7.163 24.837 0 16 0z" fill="var(--color-secondary)"/>
                    <circle cx="16" cy="16" r="6" fill="white"/>
                </svg>`,
			});

			const marker = L.marker(latLng, {
				icon: markerIcon,
				alt: markerLabel,
			}).addTo(this.map);

			const popupContent = `
                <div class="hm-map-popup">
                    <h3 class="hm-map-popup__title">${location.title}</h3>
                    ${location.address ? `<p class="hm-map-popup__address">${location.address}</p>` : ''}
                    ${location.phone ? `<p class="hm-map-popup__phone"><a href="tel:${location.phone.replace(/[^0-9+]/g, '')}">${location.phone}</a></p>` : ''}
                </div>
            `;

			marker.bindPopup(popupContent);

			this.markers.push({
				id: location.id,
				marker: marker,
				latLng: latLng,
			});
		});

		// Fit map to show all markers
		if (this.markers.length > 0) {
			this.map.fitBounds(markerBounds, { padding: [50, 50] });
		}

		// Enable scroll wheel zoom on click
		this.map.on('click', () => {
			this.map.scrollWheelZoom.enable();
		});

		// Disable scroll wheel zoom when mouse leaves
		this.elements.$mapContainer[0].addEventListener('mouseleave', () => {
			this.map.scrollWheelZoom.disable();
		});

		// Set initial map height and invalidate size
		setTimeout(() => {
			this.setMapHeight();
			this.map.invalidateSize();
		}, 250);
	}

	initCarousel() {
		this.currentOffset = 0;
		this.activeIndex = -1;

		// Card click handler
		this.elements.$locationCards.each((index, card) => {
			jQuery(card).on('click', (e) => {
				if (e.target.tagName === 'A') return;
				this.setActiveCard(index);
			});
		});

		// Arrow click handlers
		if (this.elements.$prevArrow.length) {
			this.elements.$prevArrow.on('click', () => this.scrollPrev());
		}

		if (this.elements.$nextArrow.length) {
			this.elements.$nextArrow.on('click', () => this.scrollNext());
		}

		// Initialize: activate first card and update arrows
		setTimeout(() => {
			this.setActiveCard(0);
			this.applyOffset();
			this.updateArrowVisibility();
		}, 500);
	}

	setActiveCard(index) {
		const cards = this.elements.$locationCards;
		if (index < 0 || index >= cards.length) return;

		this.activeIndex = index;

		// Update card active states
		cards.each((i, card) => {
			jQuery(card).toggleClass('is-active', i === this.activeIndex);
		});

		// Update marker active states
		const activeCard = cards[this.activeIndex];
		const activeLocationId = parseInt(activeCard.dataset.locationId, 10);

		this.markers.forEach((m) => {
			const el = m.marker.getElement();
			if (el) {
				el.classList.toggle('is-active', m.id === activeLocationId);
			}
		});

		// Center map on the active location
		const lat = parseFloat(activeCard.dataset.lat);
		const lng = parseFloat(activeCard.dataset.lng);
		if (!isNaN(lat) && !isNaN(lng)) {
			this.map.setView([lat, lng], 10, { animate: true, duration: 0.5 });
		}
	}

	scrollPrev() {
		const step = this.getCardStep();
		this.currentOffset = Math.min(0, this.currentOffset + step);
		this.applyOffset();
	}

	scrollNext() {
		const step = this.getCardStep();
		const maxOffset = this.getMaxOffset();
		this.currentOffset = Math.max(-maxOffset, this.currentOffset - step);
		this.applyOffset();
	}

	getCardStep() {
		const cards = this.elements.$locationCards;
		if (!cards.length) return 280;

		const card = cards[0];
		if (!card) return 280;

		if (!this.elements.$carouselContainer.length) return 280;

		const carouselContainer = this.elements.$carouselContainer[0];
		if (!carouselContainer) return 280;

		const style = window.getComputedStyle(carouselContainer);
		const gap = parseFloat(style.gap) || parseFloat(style.columnGap) || 16;
		return card.offsetWidth + gap;
	}

	getMaxOffset() {
		// Check if carousel elements exist
		if (!this.elements.$carousel.length || !this.elements.$carouselContainer.length) {
			return 0;
		}

		const carousel = this.elements.$carousel[0];
		const carouselContainer = this.elements.$carouselContainer[0];

		if (!carousel || !carouselContainer) {
			return 0;
		}

		const carouselStyle = window.getComputedStyle(carousel);
		const paddingLeft = parseFloat(carouselStyle.paddingLeft) || 0;
		const paddingRight = parseFloat(carouselStyle.paddingRight) || 0;

		const mapContainer = this.elements.$mapContainer[0];
		if (!mapContainer) {
			return 0;
		}

		const availableWidth = mapContainer.offsetWidth - paddingLeft - paddingRight;
		const locationsWidth = carouselContainer.scrollWidth;

		return Math.max(0, locationsWidth - availableWidth);
	}

	applyOffset() {
		if (!this.elements.$carouselContainer.length) {
			return;
		}

		this.elements.$carouselContainer.css('transform', `translateX(${this.currentOffset}px)`);

		if (this.elements.$prevArrow.length) {
			this.elements.$prevArrow.prop('disabled', this.currentOffset >= 0);
		}
		if (this.elements.$nextArrow.length) {
			this.elements.$nextArrow.prop('disabled', this.currentOffset <= -this.getMaxOffset());
		}
	}

	updateArrowVisibility() {
		if (!this.elements.$arrowsContainer.length || !this.elements.$carouselContainer.length) {
			return;
		}

		const carousel = this.elements.$carousel[0];
		const carouselContainer = this.elements.$carouselContainer[0];
		const mapContainer = this.elements.$mapContainer[0];

		if (!carousel || !carouselContainer || !mapContainer) {
			return;
		}

		const carouselStyle = window.getComputedStyle(carousel);
		const paddingLeft = parseFloat(carouselStyle.paddingLeft) || 0;
		const paddingRight = parseFloat(carouselStyle.paddingRight) || 0;

		const availableWidth = mapContainer.offsetWidth - paddingLeft - paddingRight;
		const locationsWidth = carouselContainer.scrollWidth;

		if (locationsWidth <= availableWidth) {
			this.elements.$arrowsContainer.hide();
		} else {
			this.elements.$arrowsContainer.show();
		}
	}

	setMapHeight() {
		const mapContainerParent = this.$element[0].closest('.map-container');

		if (mapContainerParent) {
			const parentHeight = mapContainerParent.offsetHeight;

			if (parentHeight > 0) {
				this.$element.css('height', parentHeight + 'px');
				const widgetContainer = this.$element.find('.elementor-widget-container');
				if (widgetContainer.length) {
					widgetContainer.css('height', parentHeight + 'px');
				}
				this.elements.$mapWrapper.css('height', parentHeight + 'px');
				this.elements.$mapContainer.css('height', parentHeight + 'px');

				if (this.map) {
					setTimeout(() => this.map.invalidateSize(), 100);
				}
			}
		}
	}

	bindResizeEvents() {
		let resizeTimeout;
		jQuery(window).on('resize', () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				this.setMapHeight();
				this.updateArrowVisibility();
				if (this.map) {
					this.map.invalidateSize();
				}
				// Clamp offset on resize
				if (this.currentOffset < -this.getMaxOffset()) {
					this.currentOffset = -this.getMaxOffset();
				}
				this.applyOffset();
			}, 250);
		});
	}
}

jQuery(window).on('elementor/frontend/init', () => {
	elementorFrontend.elementsHandler.attachHandler(
		'hm_location_map',
		HoffmasterLocationMapHandler
	);
});
